import React, { useRef } from "react";
import html2canvas from "html2canvas";

const ScoreCard = ({ score, index, openScoreDetail }) => {
  // Create a ref for the score card element
  const scoreCardRef = useRef(null);

  // Format dates
  const startTime = new Date(score.StartTime + "Z");
  const endTime = new Date(score.EndTime + "Z");

  // Format start date for display
  const gameDate = startTime.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  // Calculate time taken
  const timeDiffMs = endTime - startTime;
  const seconds = Math.floor(timeDiffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const timeTaken = `${minutes}:${remainingSeconds
    .toString()
    .padStart(2, "0")}`;

  // Calculate animation delay based on index
  const animationDelay = `${index * 0.1}s`;

  // Function to download the score card as an image
  const downloadScoreCard = (e) => {
    e.stopPropagation(); // Prevent event bubbling

    if (scoreCardRef.current) {
      // Show a brief "downloading" message
      const downloadingToast = document.createElement("div");
      downloadingToast.innerText = "Preparing download...";
      downloadingToast.style.position = "fixed";
      downloadingToast.style.bottom = "20px";
      downloadingToast.style.left = "50%";
      downloadingToast.style.transform = "translateX(-50%)";
      downloadingToast.style.padding = "10px 20px";
      downloadingToast.style.backgroundColor = "rgba(0,0,0,0.7)";
      downloadingToast.style.color = "white";
      downloadingToast.style.borderRadius = "4px";
      downloadingToast.style.zIndex = "9999";
      document.body.appendChild(downloadingToast);

      // Store the original styles
      const originalOpacity = scoreCardRef.current.style.opacity;
      const originalAnimation = scoreCardRef.current.style.animation;
      const originalTransform = scoreCardRef.current.style.transform;

      // Temporarily modify the element for better capture
      scoreCardRef.current.style.opacity = "1";
      scoreCardRef.current.style.animation = "none";
      scoreCardRef.current.style.transform = "none";

      // Use html2canvas with simplified settings
      html2canvas(scoreCardRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
      })
        .then((canvas) => {
          // Convert canvas to data URL
          const image = canvas.toDataURL("image/png", 1.0);

          // Create a download link
          const link = document.createElement("a");
          link.href = image;
          link.download = `Score_${score.Name}_${score.GameID}.png`;

          // Trigger download
          link.click();

          // Restore original styles
          scoreCardRef.current.style.opacity = originalOpacity;
          scoreCardRef.current.style.animation = originalAnimation;
          scoreCardRef.current.style.transform = originalTransform;

          // Remove toast
          document.body.removeChild(downloadingToast);
        })
        .catch((error) => {
          console.error("Error capturing score card:", error);
          alert("Failed to download score card. Please try again.");
          document.body.removeChild(downloadingToast);
        });
    }
  };

  // Function to share score card on WhatsApp
  const shareOnWhatsApp = (e) => {
    e.stopPropagation(); // Prevent event bubbling

    // Create share text with score details
    const shareText =
      `🏆 Score Card: ${score.Name}\n` +
      `📱 Mobile: ${score.MobileNo}\n` +
      `🎮 Game ID: ${score.GameID}\n` +
      `📅 Played On: ${gameDate}\n` +
      `✅ Correct Words: ${score.CorrectWords}\n` +
      `✅ Correct Sentences: ${score.CorrectSentence}\n` +
      `⏱️ Time Taken: ${timeTaken}\n` +
      `🔗 குறளாட்டம் கொண்டாட்டம்`;

    // Try using Web Share API first (modern browsers)
    if (navigator.share) {
      navigator
        .share({
          title: `${score.Name}'s Score Card`,
          text: shareText,
        })
        .then(() => console.log("Shared successfully"))
        .catch((error) => {
          console.error("Error sharing:", error);
          // Fall back to WhatsApp URL scheme
          openWhatsAppDirectly(shareText);
        });
    } else {
      // Fall back to WhatsApp URL scheme
      openWhatsAppDirectly(shareText);
    }
  };

  // Function to open WhatsApp directly with the share text
  const openWhatsAppDirectly = (shareText) => {
    // Encode the text for URL
    const encodedText = encodeURIComponent(shareText);

    // Check if it's a mobile device
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    // Create WhatsApp URL based on device type
    const whatsappUrl = isMobile
      ? `whatsapp://send?text=${encodedText}`
      : `https://web.whatsapp.com/send?text=${encodedText}`;

    // Open WhatsApp in a new window
    window.open(whatsappUrl, "_blank");
  };

  return (
    <div
      ref={scoreCardRef}
      key={score.id}
      className="relative overflow-hidden rounded-xl shadow-xl border border-gray-200 hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:translate-y-[-5px]"
      style={{
        opacity: 0,
        animation: `fadeInUp 0.6s ease-out ${animationDelay} forwards`,
        background: "linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%)",
      }}
    >
      {/* Header with gradient and buttons */}
      <div
        className="p-4 text-center relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Action buttons */}
        <div className="absolute right-3 top-3 flex space-x-2">
          {/* WhatsApp share button */}
          <button
            onClick={shareOnWhatsApp}
            className="w-8 h-8 flex items-center justify-center bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-all duration-300 focus:outline-none"
            title="Share on WhatsApp"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-white"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
          </button>

          {/* Download button */}
          <button
            onClick={downloadScoreCard}
            className="w-8 h-8 flex items-center justify-center bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-all duration-300 focus:outline-none"
            title="Download Score Card"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </button>
        </div>

        {/* Decorative circles */}
        <div className="absolute left-3 top-3 w-2 h-2 rounded-full bg-white opacity-50"></div>
        <div className="absolute left-7 top-3 w-2 h-2 rounded-full bg-white opacity-30"></div>
        <div className="absolute left-11 top-3 w-2 h-2 rounded-full bg-white opacity-10"></div>

        <h3 className="text-xl font-bold text-white tracking-wider">
          {score.Name}
        </h3>
      </div>

      {/* Content with improved spacing and typography */}
      <div className="p-6 space-y-4">
        <div className="flex justify-between items-center border-b border-gray-100 pb-1">
          <span className="text-gray-600 font-medium">Mobile:</span>
          <span className="text-gray-800 font-semibold">{score.MobileNo}</span>
        </div>
        <div className="flex justify-between items-center border-b border-gray-100 pb-1">
          <span className="text-gray-600 font-medium">Game :</span>
          <span className="text-gray-800 font-semibold">{score.GameName}</span>
        </div>
        <div className="flex justify-between items-center border-b border-gray-100 pb-1">
          <span className="text-gray-600 font-medium">Played On:</span>
          <span className="text-gray-800 font-semibold">{gameDate}</span>
        </div>
        {/* Score metrics with enhanced styling */}
        <div className="grid grid-cols-2 gap-4 my-4">
          <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-center transform transition-transform hover:scale-105">
            <div className="text-indigo-600 font-bold text-xl">
              {score.CorrectWords}
            </div>
            <div className="text-gray-500 text-sm">Correct Words</div>
          </div>
          <div className="bg-purple-50 border rounded-lg p-3 text-center transform transition-transform hover:scale-105">
            <div className="text-purple-600 font-bold text-xl">
              {score.CorrectSentence}
            </div>
            <div className="text-gray-500 text-sm">Correct Sentences</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 my-4">
          <div className="bg-blue-50 border rounded-lg p-3 text-center transform transition-transform hover:scale-105">
            <span className="t font-mono font-bold text-blue-600">
              {timeTaken} sec
            </span>

            <div className="text-gray-500 text-sm">Time Taken</div>
          </div>
          <div className="bg-blue-50 border rounded-lg p-3 text-center transform transition-transform hover:scale-105">
            <span className="t font-mono font-bold text-blue-600">
              {score.duration} sec
            </span>

            <div className="text-gray-500 text-sm">Game Duration</div>
          </div>
        </div>

        {/* View Score Detail button with enhanced styling */}
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => openScoreDetail(score)}
            className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-300 transform hover:scale-[1.02] font-semibold text-sm tracking-wide shadow-md"
          >
            View Detail
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoreCard;
